import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead from '../components/SEOHead';
import './OurWork.css';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.startsWith('/') ? '' : '/media/'}${path}`;
};

const OurWorkCategorySlider = ({ items }) => {
  const { i18n } = useTranslation();
  const gridRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const isSlider = items.length > 1;

  const updateScrollState = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const overflow = maxScroll > 1;
    setHasOverflow(overflow);
    setCanScrollLeft(overflow && el.scrollLeft > 4);
    setCanScrollRight(overflow && el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver.observe(child));

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [items, updateScrollState]);

  const scrollByCard = (direction) => {
    const el = gridRef.current;
    if (!el) return;

    const firstCard = el.querySelector('.our-work-card');
    const gap = Number.parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || '20') || 20;
    const step = firstCard ? firstCard.offsetWidth + gap : el.clientWidth * 0.8;

    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const prevLabel = i18n.language === 'ka' ? 'წინა ნამუშევარი' : 'Previous work item';
  const nextLabel = i18n.language === 'ka' ? 'შემდეგი ნამუშევარი' : 'Next work item';

  const cards = items.map((it) => (
    <Link key={it.id} to={`/history/our-work/${it.slug}`} className="our-work-card our-work-card-link">
      <div className="our-work-card-media">
        {it.photoUrl ? <img src={it.photoUrl} alt={it.title} loading="lazy" /> : <div className="our-work-card-media-placeholder" />}
      </div>
      <div className="our-work-card-body">
        <h2 className="our-work-card-title">{it.title}</h2>
        {it.short ? <p className="our-work-card-desc">{it.short}</p> : null}
      </div>
    </Link>
  ));

  if (!isSlider) {
    return <div className="our-work-grid is-static">{cards}</div>;
  }

  return (
    <div className={`our-work-slider-container ${hasOverflow ? 'has-arrows' : ''}`}>
      {hasOverflow ? (
        <button
          type="button"
          className="our-work-nav prev"
          onClick={() => scrollByCard(-1)}
          disabled={!canScrollLeft}
          aria-label={prevLabel}
        >
          ‹
        </button>
      ) : null}
      <div ref={gridRef} className="our-work-grid is-slider">
        {cards}
      </div>
      {hasOverflow ? (
        <button
          type="button"
          className="our-work-nav next"
          onClick={() => scrollByCard(1)}
          disabled={!canScrollRight}
          aria-label={nextLabel}
        >
          ›
        </button>
      ) : null}
    </div>
  );
};

const OurWork = () => {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, itemsRes] = await Promise.all([
          api.get('/content/work-categories/'),
          api.get('/content/our-work/'),
        ]);
        setCategories(categoriesRes.data?.results || categoriesRes.data || []);
        setItems(itemsRes.data?.results || itemsRes.data || []);
      } catch (e) {
        setError(i18n.language === 'ka' ? 'ნამუშევრების ჩატვირთვა ვერ მოხერხდა' : 'Failed to load our work');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  const localizedCategories = useMemo(() => {
    const isKa = i18n.language === 'ka';
    return categories.map((cat) => ({
      ...cat,
      title: (isKa ? (cat.title_ka || cat.title_en) : (cat.title_en || cat.title_ka)) || '',
    }));
  }, [categories, i18n.language]);

  const localizedItems = useMemo(() => {
    const isKa = i18n.language === 'ka';
    return items.map((it) => ({
      ...it,
      title: (isKa ? (it.title_ka || it.title_en) : (it.title_en || it.title_ka)) || '',
      short: (isKa ? (it.short_description_ka || it.short_description_en) : (it.short_description_en || it.short_description_ka)) || '',
      categoryTitle: (isKa
        ? (it.category?.title_ka || it.category?.title_en)
        : (it.category?.title_en || it.category?.title_ka)) || '',
      photoUrl: getImageUrl(it.card_photo),
    }));
  }, [items, i18n.language]);

  const categorizedSections = useMemo(() => {
    const sectionMap = new Map();

    localizedCategories.forEach((cat) => {
      sectionMap.set(String(cat.id), { id: String(cat.id), title: cat.title, items: [] });
    });

    const uncategorizedTitle = i18n.language === 'ka' ? 'კატეგორიის გარეშე' : 'Uncategorized';

    localizedItems.forEach((it) => {
      const key = String(it.category?.id || 'uncategorized');
      if (!sectionMap.has(key)) {
        sectionMap.set(key, {
          id: key,
          title: it.categoryTitle || uncategorizedTitle,
          items: [],
        });
      }
      sectionMap.get(key).items.push(it);
    });

    return Array.from(sectionMap.values()).filter((section) => section.items.length > 0);
  }, [localizedCategories, localizedItems, i18n.language]);

  return (
    <div className="our-work-page">
      <SEOHead
        title="Our Work | GFM Studio Portfolio"
        description="Browse GFM Studio's portfolio of live events, broadcasts, and productions."
        path="/history/our-work"
      />
      <div className="our-work-header">
        <h1>{i18n.language === 'ka' ? 'ჩვენი ნამუშევრები' : 'Our Work'}</h1>
      </div>

      {loading ? (
        <div className="our-work-state">{i18n.language === 'ka' ? 'იტვირთება...' : 'Loading...'}</div>
      ) : error ? (
        <div className="our-work-state">{error}</div>
      ) : (
        <>
          {categorizedSections.length === 0 ? (
            <div className="our-work-state">
              {i18n.language === 'ka' ? 'ნამუშევრები არ მოიძებნა' : 'No work found'}
            </div>
          ) : (
            categorizedSections.map((section) => (
              <section key={section.id} className="our-work-category-section">
                <h2 className="our-work-category-heading">{section.title}</h2>
                <OurWorkCategorySlider items={section.items} />
              </section>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default OurWork;
